import { useAuth0 } from '@auth0/auth0-react';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useRoutes } from 'react-router-dom';
import BannerPageWrapper from './components/banner/wrapper';
import BestPractices from './components/best-practices/page';
import CollaboratorsInvitationsPage from './components/collaborators/invitations/page';
import Collaborators from './components/collaborators/page';
import DashboardBonus from './components/dashboard/bonus/page';
import DashboardParent from './components/dashboard/page';
import EmailPageWrapper from './components/emails/wrapper';
import AccessExpired from './components/error/access-expired';
import PageNotFound from './components/error/page-not-found';
import Help from './components/help/page';
import KpiDashboardWrapper from './components/kpi-dashboard/wrapper';
import CourseBuilderWrapper from './components/learning-suite/admin/course-builder/wrapper';
import LearningSuiteAdminWrapper from './components/learning-suite/admin/wrapper';
import CourseDetailWrapper from './components/learning-suite/user/course/wrapper';
import LearningSuiteWrapper from './components/learning-suite/user/wrapper';
import OrderDetailsParent from './components/order/[id]/page';
import CreateOrder from './components/order/create/page';
import Order from './components/order/page';
import OfferCheckoutWrapper from './components/offer-checkout/wrapper';
import OffersWrapper from './components/offers/wrapper';
import OrganisationDetailParent from './components/organisations/[id]/page';
import Organisations from './components/organisations/page';
import ProductsWrapper from './components/products/wrapper';
import OnboardingPersonalStep from './components/register/checkout/step1/page';
import OnboardingPaymentStep from './components/register/checkout/step2/page';
import OnboardingWelcomeStep from './components/register/checkout/step3/page';
import RegisterInformationsParent from './components/register/informations/page';
import Logout from './components/register/logout/page';
import Register from './components/register/page';
import ResetPassword from './components/register/reset-password';
import SelfCheckout from './components/register/self-checkout/page';
import RegisterUsers from './components/register/users/page';
import VideoScreen from './components/register/video/page';
import { Context } from './components/root/context';
import HomeRedirect from './components/root/home-redirect';
import Settings from './components/settings/page';
import Support from './components/support/page';
import Templates from './components/templates/page';
import { MailVerification } from './components/verification/mail-verification';
import { useUser } from './hooks/use-user';
import Preloader from './ui/components/ui/preloader';

const Routes = () => {
  const [element, setElement] = useState(<Preloader />);
  const { isAuthenticated, user, isLoading } = useAuth0();
  const [isProfileComplete, setProfileComplete] = useState(false);
  const { user: loggedInUser } = useUser();
  const navigate = useNavigate();
  const { accessToken } = useContext(Context);

  useEffect(() => {
    if (isLoading) {
      setElement(<Preloader />);
      return;
    }
    if (!isAuthenticated) {
      return;
    }
    setProfileComplete(true);
  }, [isAuthenticated, isLoading, navigate, user]);

  const getElement = (route: string, element: JSX.Element, title: string) => {
    if (!loggedInUser) {
      return <Preloader />;
    }
    return element;
  };

  const routes = useRoutes([
    {
      path: '/collaborators',
      element: isProfileComplete ? getElement('/collaborators', <Collaborators />, 'die Kunden') : element
    },
    {
      path: '/collaborators/invitations',
      element: isProfileComplete ? getElement('/collaborators/invitations', <CollaboratorsInvitationsPage />, 'die Kundeneinladungen') : element
    },
    { path: '/help', element: isProfileComplete ? getElement('/help', <Help />, 'die Hilfe') : element },
    {
      path: '/order/create',
      element: isProfileComplete ? getElement('/order/create', <CreateOrder />, 'die Bestellung') : element
    },
    {
      path: '/order/:id',
      element: isProfileComplete ? getElement('/order/:id', <OrderDetailsParent />, 'die Bestellung') : element
    },
    {
      path: '/order',
      element: isProfileComplete ? getElement('/order', <Order />, 'die Bestellung') : element
    },
    {
      path: '/self-checkout/step1',
      element: accessToken ? <OnboardingPersonalStep /> : element
    },
    {
      path: '/self-checkout/step2',
      element: accessToken ? <OnboardingPaymentStep /> : element
    },
    {
      path: '/self-checkout/step3',
      element: accessToken ? <OnboardingWelcomeStep /> : element
    },
    {
      path: '/register/informations',
      element: accessToken ? <RegisterInformationsParent /> : element
    },
    {
      path: '/register/users',
      element: accessToken ? <RegisterUsers /> : element
    },
    {
      path: '/register/video',
      element: accessToken ? <VideoScreen /> : element
    },
    {
      path: '/settings',
      element: isProfileComplete ? getElement('/settings', <Settings />, 'die Einstellungen') : element
    },
    {
      path: '/organisations',
      element: isProfileComplete ? getElement('/organisations', <Organisations />, 'die Organisationen') : element
    },
    {
      path: '/organisations/:id',
      element: isProfileComplete ? getElement('/organisations/:id', <OrganisationDetailParent />, 'die Organisationen') : element
    },
    { path: '/products', element: isProfileComplete ? getElement('/products', <ProductsWrapper />, 'die Produkte') : element },
    { path: '/offers', element: isProfileComplete ? getElement('/offers', <OffersWrapper />, 'die Angebote') : element },
    {
      path: '/7i7-best-practices',
      element: isProfileComplete ? getElement('/7i7-best-practices', <BestPractices />, 'die Best Practices') : element
    },
    {
      path: '/templates',
      element: isProfileComplete ? getElement('/templates', <Templates />, 'die Vorlagen') : element
    },
    {
      path: '/dashboard',
      element: isProfileComplete ? getElement('/dashboard', <DashboardParent />, 'das Dashboard') : element
    },
    {
      path: '/',
      element: isProfileComplete ? getElement('/', <HomeRedirect />, 'Home') : element
    },
    {
      path: '/bonus',
      element: isProfileComplete ? getElement('/bonus', <DashboardBonus />, 'das Dashboard') : element
    },
    { path: '/emails', element: isProfileComplete ? getElement('/emails', <EmailPageWrapper />, 'die E-Mails') : element },
    { path: '/banner', element: isProfileComplete ? getElement('/banner', <BannerPageWrapper />, 'die Banner') : element },
    { path: '/kpi', element: isProfileComplete ? getElement('/kpi', <KpiDashboardWrapper />, 'das KPI Dashboard') : element },
    { path: '/learning-suite', element: isProfileComplete ? getElement('/learning-suite', <LearningSuiteWrapper />, 'die Learning Suite') : element },
    {
      path: '/learning-suite/course/:id',
      element: isProfileComplete ? getElement('/learning-suite/course/:id', <CourseDetailWrapper />, 'den Kurs') : element
    },
    {
      path: '/learning-suite/admin',
      element: isProfileComplete ? getElement('/learning-suite/admin', <LearningSuiteAdminWrapper />, 'die Learning Suite Verwaltung') : element
    },
    {
      path: '/learning-suite/admin/course/:id',
      element: isProfileComplete ? getElement('/learning-suite/admin/course/:id', <CourseBuilderWrapper />, 'der Kurs-Builder') : element
    },

    { path: '/logout', element: isAuthenticated ? <Logout /> : element },
    { path: '/verification/mail', element: <MailVerification /> },
    { path: '/support', element: <Support /> },
    { path: '/reset', element: <ResetPassword /> },
    { path: '/access-expired', element: <AccessExpired /> },
    { path: '/register', element: <Register /> },
    { path: '/self-checkout', element: <SelfCheckout /> },
    { path: '/checkout/:token', element: <OfferCheckoutWrapper /> },
    { path: '/*', element: <PageNotFound /> }
  ]);

  return routes;
};

export default Routes;
